import { useState } from "react";
import { useLang } from "../../context/LanguageContext";
import { useSite } from "../../context/SiteContext";
import { MapPin, Coffee, Utensils, Sailboat, TreePalm, ArrowUpRight } from "lucide-react";
import SithoniaGuideDialog from "./SithoniaGuideDialog";
import { trackGuideOpened } from "../../lib/analytics";

export default function Location() {
  const { lang, t } = useLang();
  const { site } = useSite();
  const [topic, setTopic] = useState(null);

  const cards = [
    { topic: "beaches", Icon: Sailboat, label_el: "Παραλίες", label_en: "Beaches", count_el: "12 κορυφαίες", count_en: "12 top picks" },
    { topic: "taverns", Icon: Utensils, label_el: "Ταβέρνες", label_en: "Taverns", count_el: "Παραδοσιακή γεύση", count_en: "Traditional flavours" },
    { topic: "cafes", Icon: Coffee, label_el: "Καφέ & Bars", label_en: "Cafés & Bars", count_el: "Brunch · sunset", count_en: "Brunch · sunset" },
    { topic: "oldVillage", Icon: TreePalm, label_el: "Παλιό Χωριό", label_en: "Old Village", count_el: "Ιστορία & κουλτούρα", count_en: "History & culture" },
  ];

  return (
    <section
      id="location"
      data-testid="location-section"
      className="py-16 sm:py-24 lg:py-32 bg-[#F5F2EB] overflow-x-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-center mb-10 sm:mb-16">
          <div className="lg:col-span-5">
            <p className="uppercase text-xs sm:text-sm tracking-[0.25em] text-[#6B705C] mb-3 sm:mb-4">
              {t.location.overline}
            </p>
            <h2 className="font-serif text-3xl sm:text-5xl lg:text-6xl text-[#1A2B4C] tracking-tight leading-[1.05] mb-6 sm:mb-8">
              {t.location.title}
            </h2>
            <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-6 sm:mb-8">
              {t.location.description}
            </p>
            <a
              href={site.maps_link}
              target="_blank"
              rel="noreferrer"
              data-testid="location-maps-link"
              className="inline-flex items-center gap-2 bg-[#1A2B4C] text-white px-5 sm:px-6 py-3 rounded-full text-xs sm:text-sm tracking-wide hover:-translate-y-0.5 transition-transform"
            >
              <MapPin className="w-4 h-4" />
              {t.location.directions}
            </a>
          </div>
          <div className="lg:col-span-7">
            <div className="overflow-hidden rounded-2xl sm:rounded-3xl shadow-xl">
              <iframe
                title="Sithonia Holidays map"
                data-testid="location-map-iframe"
                src={site.maps_embed}
                className="w-full h-[280px] sm:h-[400px] lg:h-[460px] border-0"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>

        <div>
          <p className="uppercase text-xs sm:text-sm tracking-[0.25em] text-[#6B705C] mb-3 sm:mb-4">
            {t.location.explore}
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
            {cards.map(({ topic: topicKey, Icon, label_el, label_en, count_el, count_en }) => (
              <button
                key={topicKey}
                type="button"
                data-testid={`explore-card-${topicKey}`}
                onClick={() => {
                  trackGuideOpened(topicKey);
                  setTopic(topicKey);
                }}
                className="group text-left bg-white rounded-2xl p-4 sm:p-6 border border-[#E9E2D3] hover:shadow-xl hover:-translate-y-1 transition-all duration-500 relative overflow-hidden"
              >
                <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#6B705C] mb-3 sm:mb-4 group-hover:text-[#1A2B4C] transition-colors" />
                <p className="font-serif text-base sm:text-xl text-[#1A2B4C] leading-tight">
                  {lang === "el" ? label_el : label_en}
                </p>
                <p className="text-[10px] sm:text-xs uppercase tracking-widest text-[#6B705C] mt-1.5">
                  {lang === "el" ? count_el : count_en}
                </p>
                <ArrowUpRight className="absolute top-4 right-4 w-4 h-4 text-[#6B705C] opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
              </button>
            ))}
          </div>
        </div>
      </div>

      <SithoniaGuideDialog
        topic={topic}
        open={!!topic}
        onClose={() => setTopic(null)}
      />
    </section>
  );
}
